import { Icon, spacing } from '@scality/core-ui';
import { Button, ButtonGroup } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import { searchOutsideDashboardMode } from '../../dashboard';
import { useIsDashboardMode } from '../../hooks/useIsDashboardMode';
import { useIsInternalUser } from '../../hooks/useIsInternalUser';
import { useIsNarrowViewport } from '../../hooks/useIsNarrowViewport';
import { CONVERSATION_PATH, DASHBOARDS_PATH } from '../../routes/paths';

/** Mode values are the routes they navigate to, so the group's `value` is just the current one. */
const CONVERSATION_MODE = 'conversation';
const DASHBOARD_MODE = 'dashboard';

/** Full width of the rail when expanded, a small gutter when collapsed. */
const ModeSwitcher = styled.div<{ $expanded: boolean }>`
  margin: ${spacing.r2} ${({ $expanded }) => ($expanded ? '0' : spacing.r4)} ${spacing.r8};
  padding: ${({ $expanded }) => ($expanded ? spacing.r8 : '0')};
  ${({ $expanded }) => !$expanded && 'display: flex; justify-content: center;'}

  /* Doubled class to outweigh ButtonGroup's own ".sc-button" rule. */
  && .sc-button {
    /* ButtonGroup trails the icon (built for a sort arrow); a mode is read icon-first. */
    flex-direction: row;
    /* The size of every other row in the rail — at 16px the pair no longer fit it. */
    font-size: 0.85rem;
    padding-left: ${spacing.r4};
    padding-right: ${spacing.r4};
    gap: ${spacing.r4};

  }

  /* core-ui pads the icon slot for the label it normally trails — wrong side here, and it still
     applies collapsed since iconOnly leaves the label in the DOM. The gap above spaces instead. */
  &&& .sc-button > span:first-child {
    padding-left: 0;
    padding-right: 0;
  }

  /* ButtonGroup's hover rule (0,5,0) outranks its selected one (0,4,0) and is *darker*, so the
     active mode looked switched off when pointed at. Same logic as the sidebar rows instead. */
  &&& .sc-button:hover:enabled {
    background: ${({ theme }) => theme.highlight};
    color: ${({ theme }) => theme.textPrimary};
  }

  &&& .sc-button[aria-pressed='true'] {
    background: ${({ theme }) => theme.highlight};
    color: ${({ theme }) => theme.textPrimary};
    box-shadow: inset 0 -${spacing.r2} 0 ${({ theme }) => theme.selectedActive};
    cursor: default;
  }

  /* Collapsed, the icon is the whole control: it has to sit on the rail's centre line. */
  ${({ $expanded }) =>
    !$expanded &&
    `
    && .sc-button {
      justify-content: center;
      /* Room around the glyph: collapsed, the button's own background *is* the selected state, and
         with no padding it hugged the icon. r8 rather than r12 — at r12 the group came out wider
         than the gutter left it and overhung the rail's right edge. */
      padding-left: ${spacing.r8};
      padding-right: ${spacing.r8};
      gap: 0;
    }
    `}

  ${({ $expanded }) =>
    $expanded &&
    `
    /* border-box, or the group's own border lands outside the rail and gets clipped. */
    > * { width: 100%; box-sizing: border-box; padding: 0; }

    /* core-ui wraps every button in a tooltip div, and that wrapper — not the button — is the
       group's flex item. Growing the button alone left the pair 27px short of the rail's edge,
       so the selected mode stopped before the list rows below it. */
    > * > * { flex: 1 1 0; min-width: 0; }
    button { width: 100%; min-width: 0; }
    `}
`;

type SidebarModeTabsProps = {
  /** Collapsed shows the same control stacked and icon-only, so the mode never goes off-screen. */
  isExpanded: boolean;
};

/**
 * The two top-level modes, as a single-select `ButtonGroup`. The same group turns vertical and
 * icon-only when the rail is collapsed, so the mode is stated in both states.
 *
 * Not rendered at all on a narrow viewport or for external users: nothing to switch between.
 */
export const SidebarModeTabs: React.FC<SidebarModeTabsProps> = ({ isExpanded }) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const isNarrow = useIsNarrowViewport();
  const isDashboard = useIsDashboardMode();
  const { isInternal } = useIsInternalUser();

  // Conversation is the only mode for external users — nothing to switch between.
  // Also covers the brief window before /health settles: the control appears once it does.
  if (!isInternal || isNarrow) {
    return null;
  }

  return (
    <ModeSwitcher $expanded={isExpanded}>
      <ButtonGroup
        aria-label="Mode"
        orientation={isExpanded ? 'horizontal' : 'vertical'}
        value={isDashboard ? DASHBOARD_MODE : CONVERSATION_MODE}
        onChange={(mode) =>
          navigate(
            mode === DASHBOARD_MODE
              ? DASHBOARDS_PATH
              : { pathname: CONVERSATION_PATH, search: searchOutsideDashboardMode(search) },
          )
        }
      >
        <Button
          value={CONVERSATION_MODE}
          icon={<Icon name="Pencil" />}
          label="Conversation"
          iconOnly={!isExpanded}
          {...(isExpanded ? {} : { tooltip: { overlay: 'Conversation' } })}
        />
        <Button
          value={DASHBOARD_MODE}
          icon={<Icon name="Dashboard" />}
          label="Dashboard"
          iconOnly={!isExpanded}
          {...(isExpanded ? {} : { tooltip: { overlay: 'Dashboard' } })}
        />
      </ButtonGroup>
    </ModeSwitcher>
  );
};
